import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Cookie, X, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const COOKIE_CONSENT_KEY = 'magicstay_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'magicstay_cookie_preferences';

export const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const isPt = language === 'pt';
  
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    performance: true,
    functionality: true,
    marketing: false,
  });

  const texts = {
    pt: {
      title: 'Sua privacidade é importante',
      description: 'Usamos cookies para melhorar sua experiência, analisar o uso do site e personalizar conteúdo. Você pode personalizar suas preferências ou aceitar todos os cookies.',
      acceptAll: 'Aceitar Todos',
      acceptSelected: 'Aceitar Selecionados',
      rejectNonEssential: 'Apenas Essenciais',
      customize: 'Personalizar',
      hideDetails: 'Ocultar Detalhes',
      learnMore: 'Saiba mais em nossa',
      privacyPolicy: 'Política de Privacidade',
      categories: {
        essential: {
          title: 'Cookies Essenciais',
          description: 'Necessários para o funcionamento básico do site. Não podem ser desativados.',
        },
        performance: {
          title: 'Cookies de Desempenho',
          description: 'Nos ajudam a entender como você usa o site através de análises anônimas (Google Analytics).',
        },
        functionality: {
          title: 'Cookies de Funcionalidade',
          description: 'Permitem lembrar suas preferências, como idioma e tema, para uma experiência personalizada.',
        },
        marketing: {
          title: 'Cookies de Marketing',
          description: 'Usados para exibir anúncios relevantes e medir a eficácia de campanhas publicitárias.',
        },
      },
    },
    en: {
      title: 'Your privacy matters',
      description: 'We use cookies to improve your experience, analyze site usage, and personalize content. You can customize your preferences or accept all cookies.',
      acceptAll: 'Accept All',
      acceptSelected: 'Accept Selected',
      rejectNonEssential: 'Essential Only',
      customize: 'Customize',
      hideDetails: 'Hide Details',
      learnMore: 'Learn more in our',
      privacyPolicy: 'Privacy Policy',
      categories: {
        essential: {
          title: 'Essential Cookies',
          description: 'Required for basic site functionality. Cannot be disabled.',
        },
        performance: {
          title: 'Performance Cookies',
          description: 'Help us understand how you use the site through anonymous analytics (Google Analytics).',
        },
        functionality: {
          title: 'Functionality Cookies',
          description: 'Allow us to remember your preferences, like language and theme, for a personalized experience.',
        },
        marketing: {
          title: 'Marketing Cookies',
          description: 'Used to display relevant ads and measure advertising campaign effectiveness.',
        },
      },
    },
  };

  const t = texts[isPt ? 'pt' : 'en'];

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        try {
          setPreferences(JSON.parse(savedPreferences));
        } catch (e) {
          console.error('Error parsing cookie preferences:', e);
        }
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem('magicstay_cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    
    // Apply preferences (e.g., disable Google Analytics if performance is false)
    if (!prefs.performance && typeof window.gtag === 'function') {
      window['ga-disable-GA_MEASUREMENT_ID'] = true;
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      performance: true,
      functionality: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleAcceptSelected = () => {
    saveConsent(preferences);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      performance: false,
      functionality: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot toggle essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00C2D1]/10 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-[#00C2D1]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {t.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {t.description}{' '}
                  <Link 
                    to="/privacy" 
                    className="text-[#00C2D1] hover:underline inline-flex items-center gap-1"
                    target="_blank"
                  >
                    {t.learnMore} {t.privacyPolicy}
                  </Link>
                </p>
              </div>
            </div>

            {/* Cookie Categories (Expandable) */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 overflow-hidden"
                >
                  <div className="grid gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {Object.entries(t.categories).map(([key, category]) => (
                      <div 
                        key={key}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                          key === 'essential' 
                            ? 'bg-gray-100 dark:bg-gray-800' 
                            : 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                        }`}
                        onClick={() => key !== 'essential' && togglePreference(key)}
                      >
                        <div className="flex-1 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900 dark:text-white">
                              {category.title}
                            </span>
                            {key === 'essential' && (
                              <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                {isPt ? 'Obrigatório' : 'Required'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {category.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <button
                            type="button"
                            disabled={key === 'essential'}
                            className={`relative w-11 h-6 rounded-full transition-colors ${
                              preferences[key]
                                ? 'bg-[#00C2D1]'
                                : 'bg-gray-300 dark:bg-gray-600'
                            } ${key === 'essential' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (key !== 'essential') togglePreference(key);
                            }}
                            data-testid={`cookie-toggle-${key}`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                preferences[key] ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setShowDetails(!showDetails)}
                variant="outline"
                className="sm:flex-1 h-10 text-sm border-gray-300 dark:border-gray-600"
                data-testid="cookie-customize-button"
              >
                {showDetails ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    {t.hideDetails}
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    {t.customize}
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleRejectNonEssential}
                variant="outline"
                className="sm:flex-1 h-10 text-sm border-gray-300 dark:border-gray-600"
                data-testid="cookie-reject-button"
              >
                {t.rejectNonEssential}
              </Button>

              {showDetails ? (
                <Button
                  onClick={handleAcceptSelected}
                  className="sm:flex-1 h-10 text-sm bg-[#00C2D1] hover:bg-[#00A5B5] text-white"
                  data-testid="cookie-accept-selected-button"
                >
                  {t.acceptSelected}
                </Button>
              ) : (
                <Button
                  onClick={handleAcceptAll}
                  className="sm:flex-1 h-10 text-sm bg-[#00C2D1] hover:bg-[#00A5B5] text-white"
                  data-testid="cookie-accept-all-button"
                >
                  {t.acceptAll}
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to check cookie consent
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    
    if (savedConsent && savedPreferences) {
      try {
        setConsent(JSON.parse(savedPreferences));
      } catch (e) {
        setConsent(null);
      }
    }
  }, []);

  return consent;
};

// Function to reset cookie consent (for testing or user request)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_PREFERENCES_KEY);
  localStorage.removeItem('magicstay_cookie_consent_date');
  window.location.reload();
};

export default CookieConsentBanner;
